import { Separator, TitleDescription } from "components/custom"
import { Button, CollapsibleText, DataTable, DateFormat, Tag } from "components/common"
import { useEffect, useMemo, useRef, useState } from "react"
import { useLazyGetLeadReviewsQuery } from "features/leads/api"
import { REVIEW_TYPE_CONFIG, ENTITY_OPTIONS } from "features/leads/components/addReviewLead/utils"
import { ratingColorMap } from "features/shared/data"
import { convertCamelCaseToReadable, joinBytItems, localisedValue } from "utils"
import { useTranslation } from "react-i18next"
import ReviewDetailDrawer from "features/clients/components/reviewDetailDrawer/reviewDetailDrawer"
import FlaggedLeads from "../flaggedLeads/flaggedLeads"
import UserProfileSummary from "features/shared/components/userProfileSummary"

const LeadReviewTable = ({ lead, client, getCount = () => {} }) => {
  const { t } = useTranslation()
  const columns = [
    { title: t("Review Type"), dataIndex: "reviewType", width: 180 },
    { title: t("Review Against"), dataIndex: "reviewAgainst", width: 220 },
    { title: t("Reviewer"), dataIndex: "reviewer", width: 220 },
    { title: t("Concerns"), dataIndex: "concerns", width: 240 },
    { title: t("Feedback"), dataIndex: "feedback", width: 280 },
  ]

  const [getLeadReviews, { data: reviews, isFetching, error }] = useLazyGetLeadReviewsQuery()
  const [queryObject, setQueryObject] = useState({
    inquiryIds: lead?.id,
    clientIds: client?.id,
    page: 0,
    limit: 10,
  })
  const reviewDetailRef = useRef()

  const reviewTypeMap = useMemo(
    () => REVIEW_TYPE_CONFIG().reduce((acc, item) => ({ ...acc, [item.value]: item.label }), {}),
    []
  )
  const entityTypeMap = useMemo(
    () => ENTITY_OPTIONS().reduce((acc, item) => ({ ...acc, [item.value]: item.label }), {}),
    []
  )

  useEffect(() => {
    getLeadReviews({ ...queryObject })
  }, [queryObject])

  useEffect(() => {
    getCount(reviews?.count || 0)
  }, [reviews?.count, getCount])

  const onChangePage = (page, pageSize) => {
    setQueryObject(prev => ({ ...prev, limit: pageSize, page }))
  }

  const tableData = useMemo(() => {
    const rows = reviews?.data || []
    return rows.map((item, index) => {
      const ratingColor = ratingColorMap()?.[item?.overallRating] || "#8a8a8a"
      const reviewerName = localisedValue({ object: item?.reviewer, key: "name" }) || "-"
      const reviewerMeta = joinBytItems([item?.reviewer?.designation, item?.reviewer?.city], <Separator />)
      return {
        key: item?.id || index,
        reviewType: (
          <TitleDescription
            title={
              <>
                {reviewTypeMap[item?.type] ? (
                  <Button
                    type="link"
                    size="small"
                    className="pointer text-underline p-0"
                    style={{ height: "auto", lineHeight: 1.25 }}
                    onClick={() => reviewDetailRef?.current?.showFor(item?.id)}
                    text={reviewTypeMap[item?.type]}
                  />
                ) : (
                  "-"
                )}
                &nbsp;
                {item?.isFlagged ? <FlaggedLeads lead={item?.lead} /> : null}
              </>
            }
            description={<DateFormat date={item?.createdAt} />}
          />
        ),
        reviewAgainst: (
          <TitleDescription
            title={joinBytItems([entityTypeMap[item?.entityType] || "-", item?.reviewAgainstId], <Separator />)}
            description={<DateFormat date={item?.entityCreatedAt} />}
          />
        ),
        reviewer: (
          <TitleDescription
            title={
              <>
                {reviewerName}&nbsp;
                <UserProfileSummary staffId={item?.reviewer?.id} />
              </>
            }
            description={reviewerMeta || "-"}
          />
        ),
        concerns: convertCamelCaseToReadable(item?.primaryConcern) || "-",
        feedback: (
          <TitleDescription
            title={
              item?.overallRating ? (
                <Tag type="light" color={ratingColor} pill>
                  {item?.overallRating}
                </Tag>
              ) : (
                t("Invalid {{entityType}}", { entityType: entityTypeMap[item?.entityType] })
              )
            }
            description={<>{item?.remarks && <CollapsibleText text={item?.remarks || "-"} collapsibleLength={55} />}</>}
          />
        ),
      }
    })
  }, [entityTypeMap, reviewTypeMap, reviews?.data, t])

  return (
    <>
      <DataTable
        pagination={{
          totalCount: reviews?.count || 0,
          pageOptions: [10, 25, 50],
        }}
        columns={columns}
        data={tableData}
        loading={isFetching}
        noUrlPush={true}
        error={error}
        onErrorRetry={() => getLeadReviews({ ...queryObject })}
        stickyOffset={true}
        onChangePage={onChangePage}
      />
      <ReviewDetailDrawer ref={reviewDetailRef} client={client} />
    </>
  )
}

export default LeadReviewTable
